// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { ApiOpportunityConfigurationResponse } from "../../../src/WorkflowsStore/Opportunity/OpportunityConfigurations/interfaces";

export const mockApiOpportunityConfigurationResponse = {
  enabledConfigs: {
    supervisionLevelDowngrade: {
      callToAction: "Change their supervision level in TOMIS.",
      caseNotesTitle: "Relevant Contact Codes",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "COURT", text: "Court mandates supervision at a higher level" },
        {
          key: "SCORE",
          text: "New StrongR 2.0 score cut offs mean the new supervision level is aligned with the latest assessment score",
        },
        { key: "Other", text: "Other: please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Supervision Level Downgrade",
      dynamicEligibilityText:
        "client[|s] may be supervised at a higher level than their latest risk score",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "supervisionLevelHigherThanAssessmentLevel",
          text: "Current supervision level: {{opportunity.person.supervisionLevel}}; Last risk score: {{assessmentLevel}} {{#if latestAssessmentDate}}(as of {{date latestAssessmentDate}}){{else}}(assessment date unknown){{/if}}",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-supervisionLevelDowngrade",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 4,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: true,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1fkqncNb_GNYBvRfOgij4QHw4HEdkkHHz/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["ClientProfileDetails", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 365 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify people who may be supervised at a higher level than their latest risk score and directs staff to update their supervision level in eTOMIS.\n  \nClients are surfaced if their latest risk score does not map to the corresponding supervision level as detailed below:\n\n* Low - Minimum or lower\n* Moderate - Medium or lower\n* High Property - Max or lower\n    ",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "supervisionLevelDowngrade",
      zeroGrantsTooltip: null,
    },
    usTnAnnualReclassification: {
      callToAction:
        "Review residents due for their annual reclassification and update their custody level in TOMIS.",
      caseNotesTitle: "Relevant Information For Classification",
      compareBy: [{ field: "releaseDate" }],
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Annual Reclassification",
      dynamicEligibilityText:
        "resident[|s] [is|are] eligible for their annual reclassification",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTnAtLeast12MonthsSinceLatestAssessment",
          text: "At least 12 months since last reclassification date",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is not maximum" },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-annualReclassificationReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 4,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1fkqncNb_GNYBvRfOgij4QHw4HEdkkHHz/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "Incarceration",
        "CaseNotes",
        "UsTnCommonlyUsedOverrideCodes",
      ],
      skipFormPreview: false,
      snooze: null,
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify residents who are due for annual custody reclassification and directs staff to complete & submit new classification paperwork.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "annualReclassification",
      zeroGrantsTooltip: null,
    },
    usTnAnnualReclassification2026Policy: {
      callToAction:
        "Review residents due for their annual reclassification and update their custody level in TOMIS.",
      caseNotesTitle: " Relevant Information For Classification",
      compareBy: [{ field: "releaseDate" }],
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "ERROR", text: "Error: Annual Reclass Not Due" },
        { key: "MAX", text: "Is on Maximum Custody" },
        { key: "OTHER", text: "Please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "[2026 Pilot] Annual Reclassification",
      dynamicEligibilityText:
        "resident[|s] [is|are] eligible for their annual reclassification [2026 Pilot]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTnAtLeast12MonthsSinceLatestAssessment",
          text: "At least 12 months since last reclassification date",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is not maximum" },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-annualReclassification2026PolicyReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 9,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://example.com",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 365 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usTnAtLeast12MonthsSinceLatestAssessment",
          text: "Last reclassification date within the last 12 month",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is maximum" },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify residents who are due for annual custody reclassification and directs staff to complete & submit new classification paperwork.",
      submittedTabTitle: "Pending",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "annualReclassification2026Policy",
      zeroGrantsTooltip: null,
    },
    usTnAnnualReclassification2026PolicyV2: {
      callToAction:
        "Review residents due for their annual reclassification and update their custody level in TOMIS.",
      caseNotesTitle: " Relevant Information For Classification",
      compareBy: [{ field: "releaseDate" }],
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "ERROR", text: "Error: Annual Reclass Not Due" },
        { key: "MAX", text: "Is on Maximum Custody" },
        { key: "OTHER", text: "Please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "[2026 Pilot V2] Annual Reclassification",
      dynamicEligibilityText:
        "resident[|s] [is|are] eligible for their annual reclassification [2026 Pilot V2]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTnAtLeast12MonthsSinceLatestAssessment",
          text: "At least 12 months since last reclassification date",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is not maximum" },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-annualReclassification2026PolicyV2Referrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 14,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://example.com",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 365 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usTnAtLeast12MonthsSinceLatestAssessment",
          text: "Last reclassification date within the last 12 month",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is maximum" },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify residents who are due for annual custody reclassification and directs staff to complete & submit new classification paperwork.",
      submittedTabTitle: "Pending",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "annualReclassification2026PolicyV2",
      zeroGrantsTooltip: null,
    },
    usTnCompliantReporting2025Policy: {
      callToAction: "Auto-fill referral",
      caseNotesTitle: "Other Relevant Information",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "DECF", text: "DECF: Denied, No Effort to Pay Fine and Costs" },
        {
          key: "DECR",
          text: "DECR: Denied Compliant Report due to Criminal Record",
        },
        {
          key: "DECT",
          text: "DECT: Denied Compliant Reporting, Insufficient Time in Supervision Level",
        },
        { key: "DEDF", text: "DEDF: Denied, No Effort to Pay Fees" },
        { key: "DEDU", text: "DEDU: Denied, Serious Compliance Issues" },
        {
          key: "DEIJ",
          text: "DEIJ: Compliant Reporting Not Allowed Per Court",
        },
        { key: "DEIO", text: "DEIO: Denied for CR" },
        { key: "DEIR", text: "DEIR: Denied, Failure to Report as Instructed" },
        { key: "Other", text: "Other: please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "TN-STEPS Compliant Reporting",
      dynamicEligibilityText:
        "client[|s] may be eligible for Compliant Reporting",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "onMinimumSupervisionAtLeastSixMonths",
          text: "On Low supervision level for 6+ months",
          tooltip: "",
        },
        {
          key: "usTnAssessedRiskLowCompliantOnStrongR2AfterIntakeSupervisionLevel",
          text: "Assessed Low-Compliant after starting on intake (latest assessment on {{date assessmentDate}})",
        },
        {
          key: "noSupervisionViolationReportWithin6Months",
          text: "No violations in the past 6 months",
        },
        {
          key: "noSupervisionViolationReportSinceIntakeSupervisionLevel",
          text: "No violations since being placed on community supervision",
        },
        {
          key: "usTnNoSupervisionSanctionWithin3Months",
          text: "No sanctions in the past 3 months",
        },
        {
          key: "usTnNoSupervisionSanctionSinceIntakeSupervisionLevel",
          text: "No sanctions since being placed on community supervision",
        },
        {
          key: "usTnNoArrestsSinceIntakeSupervisionLevel",
          text: "No positive arrest checks (ARRP) since being placed on community supervision",
          tooltip:
            "Client has had no arrests since being placed on supervision",
        },
        {
          key: "latestDrugTestIsNegativeOrMissing",
          text: ' {{#if (eq latestDrugScreenDate "")}}\nNo recent drug screens\n{{else}}\nPassed most recent drug screen on {{date latestDrugScreenDate}}\n{{/if}}',
          tooltip:
            "Tested negative on their most recent drug screen or have no drug tests",
        },
        {
          key: "noPositiveDrugScreensSinceIntakeSupervisionLevel",
          text: "No positive drug screens since being placed on community\nsupervision",
        },
        {
          key: "usTnNoArrestsInPast6Months",
          text: "No positive arrest checks (ARRP) in past 6 months",
          tooltip: "Client has had no arrests in the past 6 months",
        },
        {
          key: "usTnNegativeArrestCheckInPast6Months",
          text: "Negative arrest check (ARRN) on {{date record.metadata.latestNegativeArrestCheck.contactDate}}",
          tooltip: "Client has had no arrests in the past 6 months",
        },
        {
          key: "usTnAssessedNotHighOnStrongRDomains",
          text: "No high needs as of {{date assessmentDate}}",
          tooltip:
            "Have addressed or are addressing any highly assessed criminogenic needs",
        },
        {
          key: "usTnFeeScheduleOrPermanentExemption",
          text: " {{#if (eq latestFeeContactDate null)}}\nHas permanent exemption {{currentExemptions}}\n\n{{else}}\n {{#if (eq currentExemptions null)}}\nLatest FEEP on {{date latestFeeContactDate}}\n{{else}}\nLatest FEEP on {{date latestFeeContactDate}}, has permanent exemption {{currentExemptions}}\n{{/if}}\n{{/if}}",
          tooltip:
            "Clients who've had a FEEP code in the last 90 days are surfaced as eligible or have a permanent exemption",
        },
        {
          key: "usTnNoIneligibleCrOffense2025Policy",
          text: "{{#unless (or record.ineligibleCriteria.hasActiveSentence (eq null record.ineligibleCriteria.hasActiveSentence))}}No convictions for sex offenses or felony domestic-violence offenses{{/unless}}",
          tooltip: "",
        },
        {
          key: "usTnNotInDayReportingCenter",
          text: "Not in day reporting center",
        },
        {
          key: "usTnSupervisionTypeIsMisdemeanorProbationer",
          text: "On supervision as a misdemeanor probationer",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "usTnCompliantReporting2025PolicyReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 6,
      ineligibleCriteriaCopy: [
        {
          key: "onMinimumSupervisionAtLeastSixMonths",
          text: "Needs {{daysUntil minimumTimeServedDate}} more days on Low",
          tooltip: "Must be on Low supervision level for 6+ months",
        },
        {
          key: "usTnFeeScheduleOrPermanentExemption",
          text: "Confirm the client has a fee payment schedule in place if applicable",
          tooltip:
            "Clients who've had a FEEP code in the last 90 days are surfaced as eligible",
        },
        {
          key: "usTnAssessedNotHighOnStrongRDomains",
          text: "Has at least one high need domain",
          tooltip:
            "Have addressed or are addressing any highly assessed criminogenic needs",
        },
        {
          key: "usTnNegativeArrestCheckInPast6Months",
          text: "{{#if record.metadata.latestNegativeArrestCheck.contactDate}} Latest ARRN ({{date record.metadata.latestNegativeArrestCheck.contactDate}}) is more than 6 months old\n{{else}}\nNo recent ARRNs found\n{{/if}}",
          tooltip: "Client has had no arrests in the past 6 months",
        },
        {
          key: "hasActiveSentence",
          text: "Check judgement orders to confirm no convictions for sex offenses or felony domestic-violence offenses",
          tooltip:
            "Since TOMIS sentencing information can be incomplete, especially for Probation or Misdemeanor Probation cases, please double-check offenses on judgement orders",
        },
      ],
      initialHeader:
        "Review and refer eligible clients for Compliant Reporting using the autofilled paperwork.",
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1YNAUTViqg_Pgt15KsZPUiNG11Dh2TTiB/view",
      nonOmsCriteria: [
        {
          text: '{{#if (eq record.metadata.taskName "MINIMUM (LOW)")}}No pending felony charges{{/if}}',
          tooltip:
            "See VWARs submitted in current supervision period for information on potential pending felony charges",
        },
        {
          text: '{{#if (eq record.metadata.taskName "MINIMUM (LOW)")}}Has reported as instructed for the past 3 months (unless excused and documented){{/if}}',
          tooltip: "",
        },
        {
          text: '{{#if (eq record.metadata.taskName "MINIMUM (LOW)")}}Able to comply with any court / Board of Parole ordered special conditions{{/if}}',
          tooltip: "",
        },
        {
          text: '{{#if (eq record.metadata.taskName "INTAKE")}}No pending felony charges{{/if}}',
          tooltip:
            "See VWARs submitted in current supervision period for information on potential pending felony charges",
        },
        {
          text: '{{#if (eq record.metadata.taskName "INTAKE")}}Has reported as instructed since placement on community supervision (unless excused and documented){{/if}}',
        },
        {
          text: '{{#if (eq record.metadata.taskName "INTAKE")}}Able to comply with any court / Board of Parole ordered special conditions{{/if}}',
        },
        {
          text: '{{#if (eq record.metadata.taskName "INTAKE")}}Has completed required intake activities{{/if}}',
        },
        {
          text: '{{#if (eq record.metadata.taskName "MISDEMEANOR PROBATIONER")}}Has completed required intake activities{{/if}}',
        },
        {
          text: "Approved by judge if in relevant county or judicial district",
          tooltip:
            "Certain judicial districts and counties require case-by-case judge approval",
        },
        {
          text: "Upcoming sentence expiration date checked",
          tooltip:
            "There's no official policy cutoff for sentence expiration dates that are too soon for someone to be moved to Compliant Reporting",
        },
      ],
      nonOmsCriteriaHeader: "Requirements to check",
      notifications: [],
      omsCriteriaHeader: "Validated by data from TOMIS",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "SpecialConditions",
        "ClientProfileDetails",
        "FinesAndFees",
        "CaseNotes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 365 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [
        { subcategory: "MISSING_1_CRITERIA", text: "Missing 1 Criteria" },
        { subcategory: "MISSING_2_CRITERIA", text: "Missing 2 Criteria" },
        { subcategory: "MISSING_3_CRITERIA", text: "Missing 3 Criteria" },
        { subcategory: "MISSING_4_CRITERIA", text: "Missing 4 Criteria" },
      ],
      subcategoryOrderings: [
        {
          tab: "Almost Eligible",
          texts: [
            "MISSING_1_CRITERIA",
            "MISSING_2_CRITERIA",
            "MISSING_3_CRITERIA",
            "MISSING_4_CRITERIA",
          ],
        },
      ],
      subheading:
        "Compliant Reporting is a level of supervision that uses an interactive voice recognition system, rather than requiring regular face-to-face contacts. ",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "usTnCompliantReporting2025Policy",
      zeroGrantsTooltip: null,
    },
    usTnCustodyLevelDowngrade: {
      callToAction: "Review and update custody levels.",
      caseNotesTitle: "Relevant Information for Classification",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [{ key: "Other", text: "Please specify a reason" }],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Custody Level Downgrade",
      dynamicEligibilityText:
        "resident[|s] may be eligible for a custody level downgrade",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "custodyLevelHigherThanRecommended",
          text: "Custody level is higher than latest CAF score suggests",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is not maximum" },
        {
          key: "usTnIneligibleForAnnualReclassification",
          text: "Not eligible for annual reclassification",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment did not include an override",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-custodyLevelDowngradeReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 2,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1fkqncNb_GNYBvRfOgij4QHw4HEdkkHHz/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "Incarceration",
        "CaseNotes",
        "UsTnCommonlyUsedOverrideCodes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify residents who may be at a higher custody level than recommended and directs staff to complete & submit new classification paperwork based on the resident's latest CAF score.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "custodyLevelDowngrade",
      zeroGrantsTooltip: null,
    },
    usTnCustodyLevelDowngrade2026Policy: {
      callToAction: "Review and update custody levels.",
      caseNotesTitle: "Relevant Information for Classification",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [{ key: "OTHER", text: "Please specify a reason" }],
      denialText: null,
      deniedTabTitle: null,
      displayName: "[2026 Pilot] Special Reclassification - Downgrade",
      dynamicEligibilityText:
        "resident[|s] may be eligible for a Special Downgrade [2026 Pilot]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTnCustodyLevelHigherThanRecommended2026Policy",
          text: "Custody level is higher than latest CAF score suggests",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment did not include an override",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-custodyLevelDowngrade2026PolicyReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 8,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://example.com",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usTnCustodyLevelHigherThanRecommended2026Policy",
          text: "Custody level is not higher than latest CAF score suggests",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment included an override",
        },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "[2026 Pilot] This alert helps staff identify residents who may be at a higher custody level than recommended and directs staff to complete & submit new classification paperwork based on the resident's latest CAF score.",
      submittedTabTitle: "Pending",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "custodyLevelDowngrade2026Policy",
      zeroGrantsTooltip: null,
    },
    usTnCustodyLevelDowngrade2026PolicyV2: {
      callToAction: "Review and update custody levels.",
      caseNotesTitle: "Relevant Information for Classification",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [{ key: "OTHER", text: "Please specify a reason" }],
      denialText: null,
      deniedTabTitle: null,
      displayName: "[2026 Pilot V2] Special Reclassification - Downgrade",
      dynamicEligibilityText:
        "resident[|s] may be eligible for a Special Downgrade [2026 Pilot V2]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTnCustodyLevelHigherThanRecommended2026PolicyV2",
          text: "Custody level is higher than latest CAF score suggests",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment did not include an override",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-custodyLevelDowngrade2026PolicyV2Referrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 12,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://example.com",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usTnCustodyLevelHigherThanRecommended2026PolicyV2",
          text: "Custody level is not higher than latest CAF score suggests",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment included an override",
        },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "[2026 Pilot] This alert helps staff identify residents who may be at a higher custody level than recommended and directs staff to complete & submit new classification paperwork based on the resident's latest CAF score.",
      submittedTabTitle: "Pending",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "custodyLevelDowngrade2026PolicyV2",
      zeroGrantsTooltip: null,
    },
    usTnExpiration: {
      callToAction:
        "Review these clients and complete their auto-generated TEPE Note.",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "DATE", text: "DATE: Expiration date is incorrect or missing" },
        { key: "Other", text: "Other: please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Expiration",
      dynamicEligibilityText:
        "client[|s] may be on or past their expiration date",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "supervisionPastFullTermCompletionDateOrUpcoming1Day",
          text: "{{#if (eq 0 (daysPast eligibleDate))}}Expiration date is today{{else}}{{#if (eq 1 (daysPast eligibleDate))}}1 day{{else}}{{daysPast eligibleDate}} days{{/if}} past expiration date{{/if}} ({{date eligibleDate}})",
        },
        {
          key: "usTnNoZeroToleranceCodesSpans",
          text: "No zero tolerance codes since most recent sentence imposed date",
        },
        {
          key: "usTnNotOnLifeSentenceOrLifetimeSupervision",
          text: "Not on lifetime supervision or lifetime sentence",
        },
        {
          key: "supervisionPastFullTermCompletionDate",
          text: "{{#if (eq 0 (daysPast eligibleDate))}}Expiration date is today{{else}}{{#if (eq 1 (daysPast eligibleDate))}}1 day{{else}}{{daysPast eligibleDate}} days{{/if}} past expiration date{{/if}} ({{date eligibleDate}})",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-expirationReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 3,
      ineligibleCriteriaCopy: [],
      initialHeader:
        "Search for officers above to review clients who may be on or past their supervision expiration date.",
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1IpetvPM49g_c-D-HzGdf7v6QAe_z5IHn/view?usp=sharing",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["ClientProfileDetails", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 365 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify clients whose expiration dates are today or in the past. Complete a pre-populated discharge note (TEPE) and copy note to eTOMIS.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "expiration",
      zeroGrantsTooltip: null,
    },
    usTnInitialClassification: {
      callToAction: "Auto-fill paperwork",
      caseNotesTitle: "Relevant Information For Classification",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "START",
          text: "Has already had an initial classification since latest start in TDOC custody",
        },
        { key: "MAX", text: "Is on Maximum Custody" },
        { key: "OTHER", text: "Please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Initial Classification",
      dynamicEligibilityText:
        " resident[|s] may need an Initial Classification",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "notHasInitialClassificationInStatePrisonCustody",
          text: "Has not had initial classification since latest start in TDOC custody",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is not Max" },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-initialClassificationReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 7,
      ineligibleCriteriaCopy: [],
      initialHeader:
        "Complete and submit new classification paperwork for eligible residents. ",
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1fkqncNb_GNYBvRfOgij4QHw4HEdkkHHz/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: "Validated by data from TOMIS",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "Incarceration",
        "CaseNotes",
        "UsTnCommonlyUsedOverrideCodes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This tool helps staff identify residents who have not had an Initial Classification since their latest start in TDOC custody. ",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "initialClassification",
      zeroGrantsTooltip: null,
    },
    usTnInitialClassification2026Policy: {
      callToAction: "Auto-fill paperwork",
      caseNotesTitle: "Relevant Information For Classification",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "START",
          text: "Has already had an initial classification since latest start in TDOC custody",
        },
        { key: "MAX", text: "Is on Maximum Custody" },
        { key: "OTHER", text: "Please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "[2026 Pilot] Diagnostic Classification",
      dynamicEligibilityText:
        " resident[|s] may need an Diagnostic Classification [2026 Pilot]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "notHasInitialClassificationInStatePrisonCustody",
          text: "Has not had initial classification since latest start in TDOC custody",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is not Max" },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-initialClassification2026PolicyReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 10,
      ineligibleCriteriaCopy: [],
      initialHeader:
        "Complete and submit diagnostic classification paperwork for eligible residents.",
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://example.com",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "notHasInitialClassificationInStatePrisonCustody",
          text: "Already has initial classification on record",
        },
        { key: "custodyLevelIsNotMax", text: "Custody level is Max" },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This tool helps staff identify residents who have not had an Diagnostic Classification since their latest start in TDOC custody.",
      submittedTabTitle: "Pending",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "initialClassification2026Policy",
      zeroGrantsTooltip: null,
    },
    usTnSpecialCustodyLevelUpgrade2026Policy: {
      callToAction: "Review and update custody levels.",
      caseNotesTitle: "Relevant Information For Classification",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [{ key: "OTHER", text: "Please specify a reason" }],
      denialText: null,
      deniedTabTitle: null,
      displayName:
        "[2026 Pilot] Special Reclassification - Upgrade Due to Updated CAF Scoring",
      dynamicEligibilityText:
        "resident[|s] may need custody level reviewed under updated CAF [2026 Pilot]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTnCustodyLevelLowerThanRecommended2026Policy",
          text: "Custody level does not match latest CAF score",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment did not include an override",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection:
        "US_TN-specialCustodyLevelUpgrade2026PolicyReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 11,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://example.com",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usTnCustodyLevelLowerThanRecommended2026Policy",
          text: "Custody level is not lower than latest CAF suggests",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment included an override",
        },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "[2026 Pilot] This alert helps staff identify residents for whom the updated CAF suggests a higher custody level than what the resident currently has. Residents who need upgrades due to a new disciplinary infraction, for example, will not be surfaced under this option.\n\nUse of this alert will phase out over time, as all residents are classified under the updated CAF",
      submittedTabTitle: "Pending",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "specialCustodyLevelUpgrade2026Policy",
      zeroGrantsTooltip: null,
    },
    usTnSpecialCustodyLevelUpgrade2026PolicyV2: {
      callToAction: "Review and update custody levels.",
      caseNotesTitle: "Relevant Information For Classification",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [{ key: "OTHER", text: "Please specify a reason" }],
      denialText: null,
      deniedTabTitle: null,
      displayName:
        "[2026 Pilot V2] Special Reclassification - Upgrade Due to Updated CAF Scoring",
      dynamicEligibilityText:
        "resident[|s] may need custody level reviewed under updated CAF [2026 Pilot V2]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usTnCustodyLevelLowerThanRecommended2026PolicyV2",
          text: "Custody level does not match latest CAF score",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment did not include an override",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection:
        "US_TN-specialCustodyLevelUpgrade2026PolicyV2Referrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 13,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://example.com",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usTnCustodyLevelLowerThanRecommended2026PolicyV2",
          text: "Custody level is not lower than latest CAF suggests",
        },
        {
          key: "usTnLatestCafAssessmentNotOverride",
          text: "Last assessment included an override",
        },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "[2026 Pilot] This alert helps staff identify residents for whom the updated CAF suggests a higher custody level than what the resident currently has. Residents who need upgrades due to a new disciplinary infraction, for example, will not be surfaced under this option.\n\nUse of this alert will phase out over time, as all residents are classified under the updated CAF",
      submittedTabTitle: "Pending",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "specialCustodyLevelUpgrade2026PolicyV2",
      zeroGrantsTooltip: null,
    },
    usTnSuspensionOfDirectSupervision: {
      callToAction: "Generate request",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "TIME", text: "Insufficient time on supervision" },
        { key: "CASE PLAN", text: "Has not reached case plan goals" },
        {
          key: "CONDITIONS",
          text: "Special conditions incomplete / not compliant",
        },
        {
          key: "FINANCIAL",
          text: "Not meeting financial obligations to court and/or victim",
        },
        { key: "CHARGES", text: "Pending criminal charges" },
        {
          key: "JURISDICTION",
          text: "Under active supervision in another jurisdiction",
        },
        { key: "INDICTMENT", text: "Under indictment" },
        { key: "DENIAL BY DD", text: "Denied by District Director" },
        { key: "DENIAL BY CA", text: "Denied by Correctional Administrator" },
        { key: "DENIAL BY PA", text: "Denied by P&P Administrator" },
        { key: "Other", text: "Other: please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Suspension of Direct Supervision",
      dynamicEligibilityText:
        "client[|s] may be eligible for Suspension of Direct Supervision",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "onSupervisionAtLeast2YearsAndAssessedRiskLowWhileOnSupervisionAtLeast2Years",
          text: "On supervision with an overall risk score of 'minimum' for at least two years",
          tooltip:
            "On supervision with a 'minimum' risk score for two years, including time on Compliant Reporting unless removed from Compliant Reporting due to the imposition of a sanction.",
        },
        {
          key: "noSupervisionViolationReportWithin2Years",
          text: "No violation reports submitted in past two years",
          tooltip:
            "No submission of violation reports in past two years. Recidiviz uses the date of any underlying violation(s) associated with a violation report to check this eligibility requirement.",
        },
        {
          key: "usTnNoWarrantWithin2Years",
          text: "No warrants in past two years",
        },
        {
          key: "usTnNoSupervisionSanctionWithin1Year",
          text: "Has not been sanctioned in the past year",
        },
        {
          key: "atLeast12MonthsSinceMostRecentPositiveDrugTest",
          text: "Has not tested positive for any substance within the last year without a valid prescription",
        },
        {
          key: "hasFinesFeesBalanceOf0OrIsExempt",
          text: "Is current on supervision fee obligations as they apply to the current sentence",
        },
        {
          key: "latestDrugTestIsNegative",
          text: "Has successfully passed the most recent drug screen ({{date latestDrugScreenDate}})",
          tooltip:
            "Clients must have successfully passed the most recent drug screen prior to the request for Suspension of Direct Supervision. In some cases, a more recent screen may be required at the discretion of administrators or the ACCS.",
        },
        {
          key: "usTnNotOnCommunitySupervisionForLife",
          text: "Not supervised under a Community Supervision for Life (CSL) certificate",
        },
        {
          key: "usTnNoArrestsInPast2Years",
          text: "Negative arrest check on {{date record.metadata.latestNegativeArrestCheck.contactDate}}",
          tooltip: "No arrests within the last two years.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_TN-suspensionOfDirectSupervisionReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 5,
      ineligibleCriteriaCopy: [
        {
          key: "onSupervisionAtLeast2YearsAndAssessedRiskLowWhileOnSupervisionAtLeast2Years",
          text: "Within 60 days of being on supervision with an overall risk score of 'minimum' for at least two years",
          tooltip:
            "On supervision with a 'minimum' risk score for two years, including time on Compliant Reporting unless removed from Compliant Reporting due to the imposition of a sanction.",
        },
        {
          key: "hasFinesFeesBalanceOf0OrIsExempt",
          text: "Unpaid balance of ${{amountOwed}}",
        },
      ],
      initialHeader:
        "Review clients who may be eligible and complete the Direct Supervision Suspension Request form.",
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://recidiviz.org",
      nonOmsCriteria: [
        {
          text: "Has reached all goals for two consecutive offender case plans",
        },
        {
          text: "Client’s criminal activity, reintegration, job, housing, and community behavior have been assessed",
          tooltip:
            "Officers must consider the client's criminal involvement and associations, adjustment to community release, progress toward stable employment/education, housing, and pro-social behaviors within the community. ",
        },
        {
          text: "Has completed all special conditions of supervision and/or is in compliance with conditions",
        },
        {
          text: "In compliance with the agreed payment plan for the last year",
          tooltip:
            "Clients must meet all financial obligations to the court and/or victim as outlined in an agreed payment plan, or be otherwise exempted, and must be in compliance with the agreed payment plan for the previous 12 months. ",
        },
        { text: "No pending criminal charges in any jurisdiction" },
        { text: "Not under active supervision in any other jurisdiction" },
        { text: "Is not under indictment" },
      ],
      nonOmsCriteriaHeader: "Requirements to check",
      notifications: [],
      omsCriteriaHeader: "Validated by data from TOMIS",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "ClientProfileDetails",
        "SpecialConditions",
        "FinesAndFees",
        "CaseNotes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 365 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_TN",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [
        {
          subcategory: "INSUFFICIENT_TIME_ACCRUED",
          text: "Approaching Eligibility Date",
        },
        {
          subcategory: "HAS_FINES_FEES_BALANCE",
          text: "Has Fines and Fees Balance",
        },
      ],
      subcategoryOrderings: [
        {
          tab: "Almost Eligible",
          texts: ["INSUFFICIENT_TIME_ACCRUED", "HAS_FINES_FEES_BALANCE"],
        },
      ],
      subheading:
        "Suspension of Direct Supervision is a type of supervision for clients on parole that removes the requirement for clients to meet in person with officers. Clients will remain on parole and must adhere to all rules and conditions. For official policy details, see Policy #708.05.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "suspensionOfDirectSupervision",
      zeroGrantsTooltip: null,
    },
  },
} as const satisfies ApiOpportunityConfigurationResponse;
